export interface NodeLink {
  id: number
  from_node_id: number
  from_node_type: string
  to_node_id: number
  to_node_type: string
  relationship_type: string
  strength: string
  is_active: boolean
  created_by?: string
  from_title?: string
  to_title?: string
}

export interface ParentChainItem {
  id: number
  type: string
  title: string
}

export interface LinkEntry {
  link_id: number | null
  node_id: number
  node_type: string
  title: string
  relationship_type: string
  parent_chain: ParentChainItem[]
}

export interface NodeContext {
  blocks: LinkEntry[]
  blocked_by: LinkEntry[]
  supports: LinkEntry[]
  supported_by: LinkEntry[]
  related_to: LinkEntry[]
  involves: LinkEntry[]
  derived_from: LinkEntry[]
}

export interface LinkSuggestion {
  node_id: number
  node_type: string
  title: string
  score: number
}

const BASE = '/api/hierarchy/links'

async function handleResponse<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const body = await resp.text()
    throw new Error(`nodeLinkService: ${resp.status} ${body}`)
  }
  return resp.json()
}

export const nodeLinkService = {
  async createLink(
    from_id: number,
    from_type: string,
    to_id: number,
    to_type: string,
    relationship_type: string,
    strength?: string,
    created_by?: string,
    source_context?: Record<string, unknown>,
  ): Promise<NodeLink> {
    const resp = await fetch(BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        from_node_id: from_id,
        from_node_type: from_type,
        to_node_id: to_id,
        to_node_type: to_type,
        relationship_type,
        strength: strength ?? 'secondary',
        created_by: created_by ?? 'user',
        source_context,
      }),
    })
    return handleResponse<NodeLink>(resp)
  },

  async getSemanticLinks(node_id: number, node_type: string): Promise<NodeLink[]> {
    const resp = await fetch(`${BASE}/${node_type}/${node_id}`, {
      credentials: 'include',
    })
    return handleResponse<NodeLink[]>(resp)
  },

  async getContextForNode(node_id: number, node_type: string): Promise<NodeContext> {
    const resp = await fetch(`${BASE}/${node_type}/${node_id}/context`, {
      credentials: 'include',
    })
    return handleResponse<NodeContext>(resp)
  },

  async getSuggestedLinks(node_id: number, node_type: string): Promise<LinkSuggestion[]> {
    const resp = await fetch(`${BASE}/${node_type}/${node_id}/suggestions`, {
      credentials: 'include',
    })
    return handleResponse<LinkSuggestion[]>(resp)
  },

  async deleteLink(link_id: number): Promise<void> {
    const resp = await fetch(`${BASE}/${link_id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!resp.ok) {
      const body = await resp.text()
      throw new Error(`nodeLinkService: delete failed ${resp.status} ${body}`)
    }
  },
}
